import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { LocalStorageKeys } from '../../shared/helpers/app.constants';
import { AppUtils } from '../../shared/helpers/app.utils';
import { AccountService } from '../http-services/account.service';
import { AuthBypassService } from '../services/auth-bypass.service';
import { HomeStateService } from '../states/home-state.service';
import { UserProfileStateService } from '../states/user-profile.state.service';

@Injectable({
    providedIn: 'root'
})
export class GuestOrAuthGuard implements CanActivate {

    constructor(private readonly userProfileStateService: UserProfileStateService,
        private readonly homeStateService: HomeStateService,
        private readonly appUtils: AppUtils,
        private readonly router: Router,
        private readonly authBypassService: AuthBypassService,
        private readonly accountService: AccountService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const guestToken = localStorage.getItem(LocalStorageKeys.GuestToken);
        const authToken = localStorage.getItem(LocalStorageKeys.AuthToken);
        const refreshToken = localStorage.getItem(LocalStorageKeys.RefreshToken);
        const extras = { queryParams: { returnUrl: state.url } };

        if (authToken && !refreshToken) {
            localStorage.removeItem(LocalStorageKeys.AuthToken);
            this.router.navigate(['/auth/sign-in'], extras);
            return of(false);
        }

        if (authToken && this.accountService.isTokenExpired(authToken) && refreshToken && !this.accountService.isTokenExpired(refreshToken)) {
            return this.accountService.refreshToken().pipe(
                map(() => {
                    this.userProfileStateService.updateState();
                    this.homeStateService.initialState();
                    if (this.userProfileStateService.isSponsor && !state.url.startsWith('/sponsor')) {
                        this.router.navigate(['/sponsor']);
                        return false;
                    }
                    return true;
                }),
                catchError(err => {
                    console.error('Error refreshing token:', err);
                    this.router.navigate(['/auth/sign-in'], extras);
                    return of(false);
                })
            );
        }

        this.userProfileStateService.getUserDomainExamIdsState().pipe(take(1)).subscribe((domainIds => {
            if (!domainIds && authToken && !this.authBypassService.shouldSkip() && !this.userProfileStateService.isSponsor) {
                this.router.navigate(['/auth/account-setup'], extras);
            }
        }));

        if (this.accountService.isTokenAuthorized() && !this.accountService.isTokenExpired(authToken)) {
            if (this.userProfileStateService.isSponsor && !state.url.startsWith('/sponsor')) {
                this.router.navigate(['/sponsor']);
                return of(false);
            }
            return of(true);
        }

        if (guestToken || authToken) {
            if (authToken && !this.appUtils.isUserAuthenticated()) {
                localStorage.removeItem(LocalStorageKeys.AuthToken);
                this.router.navigate(['/auth/sign-in'], extras);
                return of(false);
            }
            this.userProfileStateService.getUserDomainExamIdsState().pipe(take(1)).subscribe((domainIds => {
                if (!domainIds && guestToken) {
                    this.router.navigate(['/user-preference'], extras);
                }

            }));
            return of(true);
        }

        this.router.navigate(['/auth/sign-in'], extras);
        return of(false);
    }
}
